import { useMemo, useState } from 'react';
import { MANUAL_CRON_HANDLERS, manualCronHandlerAcceptsNowMs } from '@nadoutong/contracts';
import type { CronRunHandlerOutcome, CronRunRecord } from '@nadoutong/contracts';
import { useCronRuns, useTriggerCronHandler, type CronRunFilters } from '../api/cronRuns.ts';
import {
  formatLocalTimestamp,
  localDateTimeInputValueToIso,
} from '../components/formatters.ts';
import { coerceErrorMessage, useToast } from '../components/Toast.tsx';
import { useT } from '../i18n/index.ts';
import type { I18nKey } from '../i18n/index.ts';
import {
  Button,
  Chip,
  ConfirmDialog,
  EmptyState,
  FormField,
  Input,
  PageHeader,
  Panel,
  TBody,
  Table,
  Td,
  Th,
  THead,
  Tooltip,
  Tr,
} from '../ui/index.ts';

/**
 * `/cron-runs` — control-loops tick history. Epic #109 P3.17.
 *
 * Table of recent ticks with filter chips for handler name + since
 * timestamp. Each row renders its per-handler outcome summary; a
 * `<details>` block exposes the raw outcome JSON.
 *
 * Epic #853 / #862 adds a manual-run panel: every runnable handler gets a
 * "Run now" action behind a ConfirmDialog. The trigger response is the
 * source of truth for a manual run — history rows (scheduled AND manual)
 * are recorded best-effort, so the feed may lag or miss a run.
 */

/**
 * Localised explanations for handlers the mapping table deliberately
 * excludes (the English reason also ships in the contracts catalog and in
 * the 501 error body).
 */
const EXCLUDED_REASON_KEYS: Readonly<Record<string, I18nKey>> = {
  fx_rate_refresh: 'cron_runs.manual.reason.fx_rate_refresh',
  gateway_public_canary: 'cron_runs.manual.reason.gateway_public_canary',
  supplier_concentration: 'cron_runs.manual.reason.supplier_concentration',
  usage_logs_cn_cleanup: 'cron_runs.manual.reason.usage_logs_cn_cleanup',
};

/**
 * Localised twin of `ManualCronRunnableHandler.sideEffectWarning`. The
 * contracts catalog decides WHETHER a handler warns (it is the machine-
 * readable declaration, and English ships with it); this map only supplies
 * the operator's own language. A handler that grows a warning in contracts
 * without an entry here still warns, via the generic key.
 */
const SIDE_EFFECT_WARNING_KEYS: Readonly<Record<string, I18nKey>> = {
  account_balance_alert: 'cron_runs.manual.side_effect.account_balance_alert',
  upstream_runway_alert: 'cron_runs.manual.side_effect.upstream_runway_alert',
  email_notice_reminder: 'cron_runs.manual.side_effect.email_notice_reminder',
  routing_default_parity_check: 'cron_runs.manual.side_effect.routing_default_parity_check',
  deliver_credit_alerts: 'cron_runs.manual.side_effect.deliver_credit_alerts',
  finance_pnl_materialize: 'cron_runs.manual.side_effect.finance_pnl_materialize',
  promoter_grant_expiry_scan: 'cron_runs.manual.side_effect.promoter_grant_expiry_scan',
  credit_draw_sweep: 'cron_runs.manual.side_effect.credit_draw_sweep',
  codex_capacity_alert: 'cron_runs.manual.side_effect.codex_capacity_alert',
  codex_capacity_rollup: 'cron_runs.manual.side_effect.codex_capacity_rollup',
};

function fmtMs(ms: number): string {
  if (ms < 1000) return `${ms}ms`;
  return `${(ms / 1000).toFixed(2)}s`;
}

export function CronRunsPage(): JSX.Element {
  const t = useT();
  const [handler, setHandler] = useState('');
  const [since, setSince] = useState('');

  const filters = useMemo<CronRunFilters>(() => {
    return {
      ...(handler.length > 0 ? { handler } : {}),
      ...(since.length > 0 ? { since: localDateTimeInputValueToIso(since) } : {}),
    };
  }, [handler, since]);

  const query = useCronRuns(filters);

  return (
    <section className="flex flex-col gap-6" data-testid="cron-runs-page">
      <PageHeader title={t('cron_runs.title')} sub={t('cron_runs.subtitle')} />

      <ManualRunPanel />

      <div
        data-testid="cron-runs-filters-card"
        className="flex flex-wrap items-end gap-4 rounded-lg border border-line-soft bg-card p-4"
      >
        <FormField label={t('cron_runs.filter.handler')} fieldId="cron-runs-filter-handler">
          <Input
            id="cron-runs-filter-handler"
            type="text"
            value={handler}
            onChange={(e) => setHandler(e.currentTarget.value)}
            data-testid="cron-runs-filter-handler"
            placeholder={t('cron_runs.filter.handler_placeholder')}
            className="min-w-[220px]"
          />
        </FormField>
        <FormField label={t('cron_runs.filter.since')} fieldId="cron-runs-filter-since">
          <Input
            id="cron-runs-filter-since"
            type="datetime-local"
            value={since}
            onChange={(e) => setSince(e.currentTarget.value)}
            data-testid="cron-runs-filter-since"
            className="min-w-[220px]"
          />
        </FormField>
      </div>

      <div data-testid="cron-runs-list-card" className="flex flex-col gap-4">
        {query.isLoading ? (
          <p
            className="m-0 rounded-lg border border-line-soft bg-card px-4 py-8 text-center text-dense text-paper-low"
            data-testid="cron-runs-loading"
          >
            {t('cron_runs.loading')}
          </p>
        ) : query.isError ? (
          <p
            className="m-0 rounded-lg border border-bad-line bg-bad-dim px-4 py-4 text-dense text-bad-bright"
            role="alert"
            data-testid="cron-runs-error"
          >
            {t('cron_runs.error')}
          </p>
        ) : (query.data ?? []).length === 0 ? (
          <div className="rounded-lg border border-line-soft bg-card">
            <EmptyState testId="cron-runs-empty" title={t('cron_runs.empty')} />
          </div>
        ) : (
          <RunsTable runs={query.data ?? []} />
        )}
      </div>
    </section>
  );
}

interface RunsTableProps {
  readonly runs: readonly CronRunRecord[];
}

function RunsTable({ runs }: RunsTableProps): JSX.Element {
  const t = useT();
  return (
    <Table testId="cron-runs-table">
      <THead>
        <Tr interactive={false}>
          <Th>{t('cron_runs.column.scheduled_at')}</Th>
          <Th num>{t('cron_runs.column.duration')}</Th>
          <Th num>{t('cron_runs.column.due')}</Th>
          <Th num>{t('cron_runs.column.ok')}</Th>
          <Th num>{t('cron_runs.column.fail')}</Th>
          <Th>{t('cron_runs.column.outcomes')}</Th>
        </Tr>
      </THead>
      <TBody>
        {runs.map((run) => (
          <RunRow key={run.id} run={run} />
        ))}
      </TBody>
    </Table>
  );
}

interface RunRowProps {
  readonly run: CronRunRecord;
}

function RunRow({ run }: RunRowProps): JSX.Element {
  const t = useT();
  const hasFail = run.fail_count > 0;
  return (
    <Tr data-testid={`cron-runs-row-${run.id}`} data-has-fail={hasFail ? 'true' : 'false'}>
      <Td mono>{formatLocalTimestamp(run.scheduled_at, { seconds: true })}</Td>
      <Td num emphasis>
        {fmtMs(run.duration_ms)}
      </Td>
      <Td num emphasis>
        {run.due_count}
      </Td>
      <Td num emphasis>
        {run.ok_count}
      </Td>
      <Td num>
        <span className={hasFail ? 'font-semibold text-bad' : 'text-paper'}>
          {run.fail_count}
        </span>
      </Td>
      <Td>
        <div className="flex flex-wrap gap-1">
          {run.outcomes.map((o) => (
            <OutcomeChip key={o.name} outcome={o} />
          ))}
        </div>
        {run.outcomes.length > 0 ? (
          <details className="mt-2">
            <summary className="cursor-pointer text-micro text-paper-low">
              {t('cron_runs.outcomes.expand')}
            </summary>
            <pre
              className="mt-2 max-h-60 overflow-y-auto whitespace-pre-wrap rounded bg-well p-2 font-mono text-micro text-paper"
              data-testid={`cron-runs-outcomes-${run.id}`}
            >
              {JSON.stringify(run.outcomes, null, 2)}
            </pre>
          </details>
        ) : null}
      </Td>
    </Tr>
  );
}

interface OutcomeChipProps {
  readonly outcome: CronRunHandlerOutcome;
}

function OutcomeChip({ outcome }: OutcomeChipProps): JSX.Element {
  return (
    <Chip
      tone={outcome.ok ? 'ok' : 'bad'}
      className="font-mono"
      testId={`cron-runs-outcome-chip-${outcome.name}`}
    >
      {outcome.name}
      {outcome.ok ? '' : ' · fail'}
    </Chip>
  );
}

/**
 * Manual-run panel (epic #853 / #862). One row per registered control-loop
 * handler: runnable handlers expose "Run now" behind a ConfirmDialog;
 * excluded handlers render a neutral "scheduler only" chip with the reason.
 */
function ManualRunPanel(): JSX.Element {
  const t = useT();
  const toast = useToast();
  const trigger = useTriggerCronHandler();
  const [pendingHandler, setPendingHandler] = useState<string | null>(null);
  const [nowLocal, setNowLocal] = useState('');

  const runnableCount = MANUAL_CRON_HANDLERS.filter((h) => h.runnable).length;

  // The confirm dialog is the LAST surface before an irreversible send, so the
  // warning has to reach it too — the card banner is one screen too early.
  const pendingEntry =
    pendingHandler === null
      ? undefined
      : MANUAL_CRON_HANDLERS.find((h) => h.name === pendingHandler);
  const pendingWarningKey =
    pendingEntry === undefined ? null : sideEffectWarningKeyFor(pendingEntry);

  const pendingAcceptsNowMs =
    pendingHandler !== null && manualCronHandlerAcceptsNowMs(pendingHandler);

  const confirmRun = (): void => {
    if (pendingHandler === null || trigger.isPending) {
      return;
    }
    const nowMs =
      pendingAcceptsNowMs && nowLocal.trim().length > 0
        ? Date.parse(localDateTimeInputValueToIso(nowLocal))
        : undefined;
    trigger.mutate({
      handler: pendingHandler,
      ...(nowMs !== undefined && Number.isFinite(nowMs) ? { nowMs } : {}),
    }, {
      onSuccess: (result) => {
        setPendingHandler(null);
        setNowLocal('');
        if (result.ok) {
          toast.success(
            t('cron_runs.manual.toast_success', {
              handler: result.handler,
              duration: result.duration_ms,
            }),
          );
        } else {
          toast.error(
            t('cron_runs.manual.toast_failed', {
              handler: result.handler,
              error: result.error ?? t('cron_runs.manual.unknown_error'),
            }),
          );
        }
      },
      onError: (err) => {
        setPendingHandler(null);
        toast.error(coerceErrorMessage(err));
      },
    });
  };

  return (
    <Panel
      caption={t('cron_runs.manual.title')}
      count={runnableCount}
      padded
      testId="cron-runs-manual-panel"
    >
      <p className="m-0 mb-3 text-dense text-paper-low" data-testid="cron-runs-manual-caveat">
        {t('cron_runs.manual.caveat')}
      </p>
      <ul className="m-0 grid list-none gap-2 p-0 sm:grid-cols-2 xl:grid-cols-3">
        {MANUAL_CRON_HANDLERS.map((handler) => {
          const warningKey = sideEffectWarningKeyFor(handler);
          return (
            <li
              key={handler.name}
              className="flex flex-col gap-2 rounded border border-line-soft bg-well px-3 py-2"
            >
              <div className="flex items-center justify-between gap-3">
                <span className="truncate font-mono text-dense text-paper">{handler.name}</span>
                {handler.runnable ? (
                  <Button
                    size="sm"
                    onClick={() => setPendingHandler(handler.name)}
                    data-testid={`cron-runs-manual-run-${handler.name}`}
                  >
                    {t('cron_runs.manual.run_now')}
                  </Button>
                ) : (
                  <Tooltip content={t(reasonKeyFor(handler.name))}>
                    <span
                      className="shrink-0"
                      data-testid={`cron-runs-manual-excluded-${handler.name}`}
                    >
                      <Chip tone="neutral" lamp={false}>
                        {t('cron_runs.manual.scheduler_only')}
                      </Chip>
                    </span>
                  </Tooltip>
                )}
              </div>
              {warningKey !== null ? (
                <p
                  className="m-0 rounded border border-warn-line bg-warn-dim/50 px-2 py-1 text-micro text-warn"
                  role="note"
                  data-testid={`cron-runs-manual-side-effect-${handler.name}`}
                >
                  {t(warningKey)}
                </p>
              ) : null}
            </li>
          );
        })}
      </ul>
      <ConfirmDialog
        open={pendingHandler !== null}
        onOpenChange={(open) => {
          if (!open && !trigger.isPending) {
            setPendingHandler(null);
            setNowLocal('');
          }
        }}
        title={t('cron_runs.manual.confirm_title', { handler: pendingHandler ?? '' })}
        body={
          pendingWarningKey === null && !pendingAcceptsNowMs ? (
            t('cron_runs.manual.confirm_body')
          ) : (
            // Warning FIRST: the idempotence sentence answers a different and
            // still-real question (will this collide with the schedule?), but
            // read first it reads as "this is safe". Reassurance goes last.
            <div className="flex flex-col gap-3">
              {pendingWarningKey !== null ? (
                <p
                  className="m-0 rounded border border-warn-line bg-warn-dim/50 px-2.5 py-2 text-dense text-warn"
                  data-testid="cron-runs-manual-confirm-side-effect"
                >
                  {t(pendingWarningKey)}
                </p>
              ) : null}
              <p className="m-0">{t('cron_runs.manual.confirm_body')}</p>
              {pendingAcceptsNowMs ? (
                <FormField
                  label={t('cron_runs.manual.now_ms_label')}
                  fieldId="cron-runs-manual-now-ms"
                  hint={t('cron_runs.manual.now_ms_hint')}
                >
                  <Input
                    id="cron-runs-manual-now-ms"
                    type="datetime-local"
                    value={nowLocal}
                    onChange={(event) => setNowLocal(event.currentTarget.value)}
                    data-testid="cron-runs-manual-now-ms"
                  />
                </FormField>
              ) : null}
            </div>
          )
        }
        confirmLabel={t('cron_runs.manual.confirm_action')}
        cancelLabel={t('common.cancel')}
        closeLabel={t('common.close')}
        onConfirm={confirmRun}
        loading={trigger.isPending}
        testId="cron-runs-manual-confirm"
      />
    </Panel>
  );
}

function reasonKeyFor(handlerName: string): I18nKey {
  return EXCLUDED_REASON_KEYS[handlerName] ?? 'cron_runs.manual.reason.generic';
}

/**
 * `null` when the catalog entry declares no outward side effect — which is
 * the case for all but a couple of handlers, so most rows render exactly as
 * before.
 */
function sideEffectWarningKeyFor(
  handler: (typeof MANUAL_CRON_HANDLERS)[number],
): I18nKey | null {
  if (!('sideEffectWarning' in handler)) {
    return null;
  }
  return SIDE_EFFECT_WARNING_KEYS[handler.name] ?? 'cron_runs.manual.side_effect.generic';
}
